/**
 * MemberMessageComposer — the 1:1 email / SMS composer to one member.
 *
 * Extracted from the enrollment page's Conversations tab (EnrollmentDetailNew)
 * so the member's activity view (TRUS-762) can open the same composer from its
 * Add menu instead of a second copy. Owns everything the send needs: the org's
 * templates, the sender identity (org address or the user's own Outlook),
 * merge-field inputs, SMS media attachments, and the send itself.
 *
 * Merge fields are filled by the server from the org's template-variable
 * registry. Input variables are asked for here; a member the server could not
 * fill a variable for comes back in `failed` and is shown, not sent.
 */
import React, { useEffect, useRef, useState } from 'react';
import { FileText, Loader2, Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ACCENT_BUTTON } from '@/components/ui/accent';
import { MediaAttachmentPicker, AttachedMediaDisplay } from '@/components/media/MediaAttachmentPicker';
import type { SelectedAttachment } from '@/components/media/MediaAttachmentPicker';
import {
  VariablePicker,
  TemplatePreviewButton,
  VariableInputFields,
  UnfilledMergeFields,
  insertToken,
  unfilledFailures,
  useSendInputs,
  type UnfilledSendFailure,
} from '@/components/messaging/templateVariables';
import { config } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';

export type ComposerChannel = 'email' | 'sms';

export interface ComposerMember {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone_no?: string | null;
  phone_number?: string | null;
}

interface Props {
  member: ComposerMember;
  channel: ComposerChannel;
  enrollmentId?: number | null;
  programId?: number | null;
  onCancel: () => void;
  /** After a successful send (the message is recorded). */
  onSent?: () => void;
  /** Hide the composer's own heading (when a dialog already titles it). */
  hideHeading?: boolean;
}

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
  'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
});

export function MemberMessageComposer({
  member, channel, enrollmentId, programId, onCancel, onSent, hideHeading = false,
}: Props) {
  const { t } = useAppTranslation();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [blockedSends, setBlockedSends] = useState<UnfilledSendFailure[]>([]);
  // 'org' = the organization's SendGrid sender (default); 'outlook' = the staff
  // user's own connected Outlook mailbox via Microsoft Graph, so the member sees
  // a reply-able personal address. Offered only once a mailbox is connected.
  const [emailSender, setEmailSender] = useState<'org' | 'outlook'>('org');
  const [outlookConn, setOutlookConn] = useState<{ connected: boolean; email?: string } | null>(null);
  const [showAttachmentPicker, setShowAttachmentPicker] = useState(false);
  const [smsAttachments, setSmsAttachments] = useState<SelectedAttachment[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const sendInputs = useSendInputs({ subject: channel === 'email' ? subject : null, body });

  const memberName = `${member.first_name || ''} ${member.last_name || ''}`.trim();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!localStorage.getItem('active_cbo_id')) return;
        const res = await fetch(`${config.weaveinApi.baseUrl}/api/communication-templates`, { headers: authHeaders() });
        if (res.ok && !cancelled) {
          const data = await res.json();
          // The backend returns the array directly, not { templates: [...] }.
          setTemplates(Array.isArray(data) ? data : data.templates || []);
        }
      } catch (e) {
        console.error('Error fetching templates:', e);
      }
    })();
    if (channel === 'email') {
      (async () => {
        try {
          const res = await fetch(
            `${config.weaveinApi.baseUrl}/api/staff/member-communications/outlook-connection-status`,
            { headers: authHeaders() },
          );
          if (cancelled) return;
          if (!res.ok) { setOutlookConn({ connected: false }); return; }
          const data = await res.json();
          setOutlookConn(data);
          if (!data?.connected) setEmailSender('org');
        } catch {
          if (!cancelled) setOutlookConn({ connected: false });
        }
      })();
    }
    return () => { cancelled = true; };
  }, [channel]);

  const selectTemplate = (id: number) => {
    setSelectedTemplate(id);
    const tmpl = templates.find((x) => x.id === id);
    if (tmpl) {
      // Keep the {{variables}}: the server fills them for this member when it
      // sends (and refuses text it cannot fill). Preview shows the result first.
      setSubject(tmpl.subject || '');
      setBody(tmpl.body || '');
    }
  };

  const reset = () => {
    setSubject('');
    setBody('');
    setSelectedTemplate(null);
    setSmsAttachments([]);
    sendInputs.reset();
  };

  const send = async () => {
    setError(null);
    if (channel === 'email' && !subject.trim()) {
      setError(t('composer.subjectRequired', 'Enter an email subject.'));
      return;
    }
    if (!body.trim()) {
      setError(t('composer.bodyRequired', 'Enter a message.'));
      return;
    }
    if (sendInputs.missing.length > 0) {
      setError(
        t('templateVariables.inputs.missing', 'Fill in the required values: {{labels}}', {
          labels: sendInputs.missing.map((m) => m.label || m.key).join(', '),
        }),
      );
      return;
    }
    setBlockedSends([]);
    setSending(true);
    try {
      let endpoint: string;
      let payload: Record<string, any>;
      if (channel === 'email') {
        const useOutlook = emailSender === 'outlook' && outlookConn?.connected;
        endpoint = useOutlook
          ? `${config.weaveinApi.baseUrl}/api/staff/member-communications/send-email-via-outlook`
          : `${config.weaveinApi.baseUrl}/api/staff/member-communications/send-email`;
        payload = {
          member_ids: [member.id],
          subject,
          message_body: body,
          enrollment_id: enrollmentId ?? undefined,
          program_id: programId ?? undefined,
          requires_response: false,
          variable_inputs: sendInputs.variableInputs,
        };
      } else {
        endpoint = `${config.weaveinApi.baseUrl}/api/staff/member-communications/send-sms`;
        payload = {
          member_ids: [member.id],
          message_body: body,
          enrollment_id: enrollmentId ?? undefined,
          program_id: programId ?? undefined,
          requires_response: false,
          variable_inputs: sendInputs.variableInputs,
          attachment_ids: smsAttachments.length > 0 ? smsAttachments.map((a) => a.asset.id) : undefined,
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setError(t('composer.sendFailedDetail', 'Could not send the message: {{detail}}', {
          detail: err.detail || t('composer.unknownError', 'unknown error'),
        }));
        return;
      }
      const result = await response.json();
      const blocked = unfilledFailures(result.failed);
      if (blocked.length > 0 && !(result.total_sent > 0)) {
        // Nothing was sent or recorded: show which variable and why, keep the draft.
        setBlockedSends(blocked);
        return;
      }
      if (!(result.total_sent > 0) && result.failed && result.failed.length > 0) {
        setError(t('composer.sendFailedDetail', 'Could not send the message: {{detail}}', {
          detail: result.failed[0].error,
        }));
        return;
      }
      toast.success(t('composer.sent', 'Message sent'));
      reset();
      onSent?.();
    } catch (e) {
      console.error('Error sending message:', e);
      setError(t('composer.sendFailed', 'Could not send the message.'));
    } finally {
      setSending(false);
    }
  };

  const channelTemplates = templates.filter((tmpl) => tmpl.communication_method === channel);

  return (
    <div className="space-y-4">
      {!hideHeading && (
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-ink">
            {channel === 'email' ? t('composer.composeEmail', 'Compose email') : t('composer.composeSms', 'Compose text message')}
          </h3>
        </div>
      )}

      {channel === 'email' && (
        <div>
          <Label htmlFor="composer-sender">{t('composer.sendFrom', 'Send from')}</Label>
          <Select
            value={emailSender}
            onValueChange={(v) => setEmailSender(v as 'org' | 'outlook')}
            disabled={!outlookConn?.connected}
          >
            <SelectTrigger id="composer-sender">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="org">{t('composer.senderOrg', 'Organization (default)')}</SelectItem>
              {outlookConn?.connected && (
                <SelectItem value="outlook">
                  {t('composer.senderOutlook', 'My Outlook — {{email}}', { email: outlookConn.email || '' })}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <p className="text-xs text-ink-500 mt-1">
            {outlookConn?.connected
              ? emailSender === 'outlook'
                ? t('composer.senderOutlookHelp', 'The member sees {{email}} and can reply to you directly.', { email: outlookConn.email || '' })
                : t('composer.senderOrgHelp', 'Sent from the organization address. Replies go to the shared inbox.')
              : t('composer.connectOutlookHelp', 'Connect your Outlook account in Settings → Connected Services to send as yourself.')}
          </p>
        </div>
      )}

      <div>
        <Label htmlFor="composer-template">{t('composer.useTemplate', 'Use template (optional)')}</Label>
        <Select
          value={selectedTemplate?.toString() || '__none__'}
          onValueChange={(value) => value !== '__none__' && selectTemplate(Number(value))}
        >
          <SelectTrigger id="composer-template">
            <SelectValue placeholder={t('composer.noTemplate', '-- No template --')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">{t('composer.noTemplate', '-- No template --')}</SelectItem>
            {channelTemplates.map((tmpl) => (
              <SelectItem key={tmpl.id} value={tmpl.id.toString()}>
                {tmpl.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {channel === 'email' && (
        <div>
          <Label htmlFor="composer-subject">{t('composer.subject', 'Subject')}</Label>
          <Input id="composer-subject" value={subject} onChange={(e) => setSubject(e.target.value)} required />
        </div>
      )}

      <div>
        <Label htmlFor="composer-body">{t('composer.message', 'Message')}</Label>
        <textarea
          id="composer-body"
          ref={bodyRef}
          className="flex min-h-[150px] w-full rounded-md border border-v2border bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2accent"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
          placeholder={channel === 'sms'
            ? t('composer.smsPlaceholder', 'Type your text message here...')
            : t('composer.emailPlaceholder', 'Type your email message here...')}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <VariablePicker appliesTo="messages" onInsert={(key) => setBody((b) => insertToken(bodyRef.current, b, key))} />
        <TemplatePreviewButton
          appliesTo="messages"
          subject={channel === 'email' ? subject : null}
          body={body}
          member={{ id: member.id, name: memberName }}
          context={{ enrollment_id: enrollmentId ?? null, program_id: programId ?? null }}
          variableInputs={sendInputs.values}
        />
      </div>
      <VariableInputFields
        inputs={sendInputs.inputs}
        values={sendInputs.values}
        onChange={sendInputs.setValues}
        idPrefix="member-composer-input"
      />
      {blockedSends.length > 0 && (
        <UnfilledMergeFields failures={blockedSends} memberNames={{ [member.id]: memberName }} />
      )}

      {channel === 'sms' && (
        <p className={`text-xs ${body.length > 160 ? 'text-red-600' : 'text-ink-500'}`}>
          {t('composer.charCount', '{{count}} characters', { count: body.length })}
          {body.length > 160 && ` ${t('composer.multiSegment', '(multiple SMS segments)')}`}
        </p>
      )}

      {channel === 'sms' && (
        <div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowAttachmentPicker(true)}>
              <FileText className="h-4 w-4 mr-1" />
              {t('composer.attachMedia', 'Attach from Media Library')}
            </Button>
            {smsAttachments.length > 0 && (
              <span className="text-xs text-ink-500">
                {t('composer.attachedCount', '{{count}} file(s) attached — sent as short links', { count: smsAttachments.length })}
              </span>
            )}
          </div>
          {smsAttachments.length > 0 && (
            <AttachedMediaDisplay
              attachments={smsAttachments}
              onRemove={(assetId) => setSmsAttachments((prev) => prev.filter((a) => a.asset.id !== assetId))}
            />
          )}
          <MediaAttachmentPicker
            isOpen={showAttachmentPicker}
            onClose={() => setShowAttachmentPicker(false)}
            onSelect={(attachments) => setSmsAttachments(attachments)}
            multiple={true}
            allowedTypes={['document', 'image', 'video']}
          />
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => { reset(); onCancel(); }} disabled={sending}>
          {t('composer.cancel', 'Cancel')}
        </Button>
        <Button
          className={ACCENT_BUTTON}
          onClick={send}
          disabled={sending || !body.trim() || (channel === 'email' && !subject.trim())}
        >
          {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : channel === 'email' ? <Mail className="h-4 w-4 mr-2" /> : <MessageSquare className="h-4 w-4 mr-2" />}
          {channel === 'email' ? t('composer.sendEmail', 'Send email') : t('composer.sendSms', 'Send text')}
        </Button>
      </div>
    </div>
  );
}

export default MemberMessageComposer;
